import React, { useEffect, useCallback } from "react";
import {
  StyleSheet,
  Pressable,
  View,
  TouchableOpacity,
  SectionList,
  Image,
} from "react-native";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useDispatch, useSelector } from "react-redux";
import LinearGradient from "react-native-linear-gradient";

//Custom Import
import color from "@constants/color";
import { scale } from "@utils/scale";
import Error from "@assets/svg/Error";
import t from "@constants/localization";
import BackArrow from "@assets/svg/BackArrow";
import { LIVE } from "@constants/contestTypes";
import RightArrow from "@assets/svg/RightArrow";
import CustomText from "@components/CustomText";
import CircularLoader from "../Story/CircularLoader";
import contestAction from "@actions/contest.action";
import { getDayMonthTime } from "@utils/getFormattedDate";
import contestEvents, { CONTEST_TYPES } from "./ContestEvents";
import WatchUpcoming from "@assets/svg/Contest/WatchUpcoming";
import { CONTEST_EVENTS } from "@constants/analyticsEventsCT";
import LiveContestIcon from "@assets/svg/Contest/LiveContestIcon";
import { liveUpcomingDataSelector } from "@selectors/contest.selector";
import {
  ConestSectionSeparator,
  ContestEmptyComponent,
} from "./Components/ContestEmptyComponent";
import { fontFamily } from "@utils/styles";
import { getTime } from "@utils/getHindiDate";

const PATH = "screens/Contest";
dayjs.extend(duration);
const Icon = {
  LIVE: LiveContestIcon,
  UPCOMING: WatchUpcoming,
};
const languageSelector = (state) => state?.auth?.user?.language;

const ClContest = ({ navigation }) => {
  const dispatch = useDispatch();
  const language = useSelector(languageSelector);
  const { data = {}, api = {} } = useSelector(liveUpcomingDataSelector);
  const { fetched = false, pending = false } = api || {};

  useEffect(() => {
    dispatch(contestAction.getLiveUpcomingContest.pending());
  }, []);

  const onBackClick = () => {
    navigation.goBack();
  };

  const clickPastContest = () => {
    contestEvents(CONTEST_EVENTS.PAST_CONTEST_BUTTON_CLICKED, {
      screen: "Contest list page",
    });
    navigation.navigate("PastContest");
  };

  const getNextTime = (next_time) => {
    const next_time_ref = dayjs(next_time),
      current_time = Date.now(),
      en = language === "en";

    let converted_time_obj = dayjs.duration(
      next_time_ref.diff(dayjs(current_time), "h", true),
      "h",
      true,
    );
    if (converted_time_obj.get("h") <= 0) {
      return en
        ? `${Math.abs(converted_time_obj.get("m"))} minute`
        : `${Math.abs(converted_time_obj.get("m"))} मिनट`;
    } else {
      return en
        ? `${Math.abs(converted_time_obj.get("h"))} hour`
        : `${Math.abs(converted_time_obj.get("h"))} घंटे`;
    }
  };

  const onPressContestItem = useCallback((item, state) => {
    const {
      contest_id,
      contest_name,
      start_time,
      end_time,
      contest_type,
      payout_time,
      can_only_view,
    } = item;
    contestEvents(CONTEST_EVENTS.CONTEST_CLICKED, {
      contest_id,
      state: state,
      title: contest_name,
      from_date: start_time,
      to_date: end_time,
      eligible: !can_only_view,
      contest_type: CONTEST_TYPES[contest_type],
      payout_sent: new Date() > new Date(payout_time) ? "Yes" : "No",
    });
    navigation.navigate("ContestOverview", {
      contest_id,
      isPastContest: false,
    });
  }, []);

  const contestDays = ({ section: { title = "", icon = "" } }) => {
    const TimeIcon = Icon[icon];
    return (
      <View style={styles.sectionTitleContainer}>
        <TimeIcon />
        <CustomText
          variant="entityHeading"
          newfont={true}
          numberOfLines={1}
          style={styles.sectionTitle}
        >
          {title}
        </CustomText>
      </View>
    );
  };

  const _keyExtractor = (item, index) => `${item?.id}${index}`;

  const renderContestItem = ({ item, section }) => {
    const {
      contest_name,
      description,
      start_time,
      uri,
      contest_id,
      processing_times,
      can_only_view = false,
      show_processing_times = false,
    } = item;
    return (
      <Pressable
        onPress={() => onPressContestItem(item, section?.type)}
        style={styles.cardContainer}
      >
        {item.contest_banner ? (
          <Image
            resizeMode={"stretch"}
            style={styles.liveBannerImage}
            source={{ uri: item.contest_banner }}
          />
        ) : (
          <LinearGradient
            colors={[color.darkSkyBlue, color.trans_209_248]}
            style={styles.bannerContainer}
            useAngle={true}
          >
            <CustomText
              variant="title_18"
              newfont={true}
              numberOfLines={3}
              style={styles.cardTitle}
            >
              {contest_name}
            </CustomText>
            <CustomText
              variant="caption_2"
              newfont={true}
              numberOfLines={4}
              style={styles.cardDescription}
            >
              {description}
            </CustomText>
            <View style={styles.contentContainer} />
            <Image
              source={require("../../assets/images/contest-celebration.png")}
              style={styles.bannerImage}
            />
          </LinearGradient>
        )}
        {section.icon === LIVE && (show_processing_times || can_only_view) ? (
          <React.Fragment>
            {can_only_view ? (
              <View style={styles.eligibleContainer}>
                <Error />
                <CustomText
                  variant="subtitle_16"
                  newfont={true}
                  numberOfLines={1}
                  style={styles.eligibleText}
                >
                  {t(`${PATH}/contestDetails/notEligible`)}
                </CustomText>
              </View>
            ) : (
              <View style={styles.lastAndNext}>
                <CustomText
                  variant="caption_2"
                  newfont={true}
                  numberOfLines={1}
                  style={styles.lastUpdateText}
                >
                  {`${t(`${PATH}/contestUpdateTime`)} ${getTime(
                    processing_times?.last,
                  )}`}
                </CustomText>
                <CustomText
                  variant="caption_2"
                  newfont={true}
                  numberOfLines={1}
                  style={styles.lastUpdateText}
                >
                  {t(`${PATH}/nextUpdateTime`, {
                    time: processing_times?.next
                      ? getNextTime(processing_times.next)
                      : "",
                  })}
                </CustomText>
              </View>
            )}
          </React.Fragment>
        ) : null}
        <View style={styles.cardBottomStrip}>
          <CustomText
            variant="subtitle_16"
            newfont={true}
            numberOfLines={1}
            style={styles.stripDate}
          >
            {section?.icon === LIVE
              ? t(`${PATH}/seeContest`)
              : start_time
                ? getDayMonthTime(start_time)
                : ""}
          </CustomText>
          <RightArrow
            fill={color.modalBackground}
            stroke={color.modalBackground}
          />
        </View>
      </Pressable>
    );
  };

  return (
    <React.Fragment>
      <View style={styles.headerContainer}>
        <View style={styles.headerTitleContainer}>
          <TouchableOpacity activeOpacity={0.8} onPress={onBackClick}>
            <BackArrow />
          </TouchableOpacity>
          <CustomText
            variant="title_20"
            newfont={true}
            numberOfLines={1}
            style={styles.textLeftPadding}
          >
            {t(`${PATH}/Contest`)}
          </CustomText>
        </View>
        <TouchableOpacity
          onPress={clickPastContest}
          activeOpacity={0.8}
          style={styles.pastContainer}
        >
          <CustomText
            variant="body_text_4"
            newfont={true}
            numberOfLines={1}
            style={styles.pastText}
          >
            {t(`${PATH}/pastContest`)}
          </CustomText>
          <View style={styles.rightBackArrow}>
            <RightArrow
              fill={color.modalBackground}
              stroke={color.modalBackground}
            />
          </View>
        </TouchableOpacity>
      </View>
      <View style={styles.container}>
        {!fetched && pending ? (
          <View style={styles.loaderStyle}>
            <CircularLoader />
          </View>
        ) : (
          <SectionList
            removeClippedSubviews
            sections={data}
            keyExtractor={_keyExtractor}
            renderItem={renderContestItem}
            contentContainerStyle={styles.listContainer}
            renderSectionHeader={contestDays}
            ListEmptyComponent={ContestEmptyComponent}
            style={styles.mainListContainer}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            SectionSeparatorComponent={ConestSectionSeparator}
          />
        )}
      </View>
    </React.Fragment>
  );
};

export default ClContest;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mainListContainer: {
    flex: 1,
  },
  listContainer: {
    paddingHorizontal: 16,
    backgroundColor: color.white,
    flexGrow: 1,
  },
  headerContainer: {
    flexDirection: "row",
    height: 56,
    paddingHorizontal: scale(16),
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: color.white,
    elevation: 8,
    shadowColor: color.mediumBlack,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
  },
  headerTitleContainer: {
    alignItems: "center",
    flexDirection: "row",
  },
  textLeftPadding: {
    paddingLeft: scale(23),
    color: color.mediumBlack,
  },
  pastText: {
    color: color.mediumBlack,
  },
  pastContainer: {
    flexDirection: "row",
    paddingHorizontal: scale(12),
    paddingVertical: 4,
    borderRadius: 4,
    borderColor: color.lighterBlack,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  rightBackArrow: {
    marginStart: scale(11),
  },
  sectionTitleContainer: {
    flexDirection: "row",
    paddingVertical: 10,
    alignItems: "center",
  },
  sectionTitle: {
    color: color.mediumBlack,
    marginBottom: 0,
    paddingHorizontal: 0,
    marginStart: 8,
  },
  cardContainer: {
    backgroundColor: color.white,
    elevation: 8,
    shadowColor: color.mediumBlack,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
    marginBottom: 10,
    borderRadius: 10,
    overflow: "hidden",
  },
  liveBannerImage: {
    height: 167,
    backgroundColor: color.white,
  },
  bannerContainer: {
    height: 167,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    flexDirection: "column",
    paddingHorizontal: 16,
  },
  bannerImage: {
    width: 174,
    height: 163,
    position: "absolute",
    end: 0,
    alignSelf: "center",
  },
  cardTitle: {
    color: color.mediumBlack,
    maxWidth: scale(161),
    lineHeight: 24,
    marginTop: 30,
  },
  cardDescription: {
    color: color.mediumBlack,
    maxWidth: scale(161),
    marginTop: 26,
  },
  cardBottomStrip: {
    height: 44,
    paddingHorizontal: 16,
    paddingVertical: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 1,
    borderColor: color.blank_grey,
  },
  stripDate: {
    color: color.mediumBlack,
  },
  loaderStyle: {
    position: "absolute",
    alignSelf: "center",
    zIndex: 100,
  },
  lastAndNext: {
    flexDirection: "row",
    alignContent: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    marginVertical: 10,
  },
  lastUpdateText: {
    color: color.lightBlack,
    fontFamily: fontFamily.medium,
  },
  eligibleContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    marginVertical: 10,
    alignContent: "center",
  },
  eligibleText: {
    color: color.mediumBlack,
    marginStart: 11,
  },
});
